import { CONSOLE_HASH_ROOT } from '@/utils/consoleEntry'

export type TabKey = 'disp' | 'fopt' | 'trace' | 'mass' | 'inb' | 'erp' | 'obs' | 'wh'

export type TabDef = {
  key: TabKey
  label: string
  heading: string
  sub: string
  sections: { id: string; label: string }[]
}

/**
 * 1차 범위(핸드오프 §1). 시안의 개요·단건추적·컷오프·입고반품은 신규 BE 조회가 필요해 빠졌다.
 * sections 의 id 는 각 탭 컴포넌트가 렌더하는 SectionHeading 의 id 와 1:1 로 맞아야 한다 —
 * 사이드 내비 클릭과 스크롤 스파이가 그 id 로 붙는다.
 *
 * 범위 축소(2026-09-14) — 탭 6개 → 5개. `재고` 탭(`mst`, `s-mst-sls`)을 통째로 뺐다 — 어드민
 * `src/pages/inventory/slsInventoryStatus`(st06)가 같은 `GET /inventory/statuses/sls` 를 이미
 * 보여준다. `설정`(`cfg`) 탭도 `s-cfg-priority`(센터 순위) 섹션을 뺐다 — 어드민
 * `outboundSchedule`·`outboundSchedule2` 의 `AllocationPriorityModal`·`OverSeasPriorityModal` 과
 * 중복이다. `s-cfg-capacity`(캐파 기본값)·`s-cfg-consumer`(Kafka 컨슈머 정지)는 대응하는 어드민
 * 화면이 없어 그대로 남는다.
 *
 * 범위 축소(2026-09-15, 사용자 결정) — 탭 5개 → 3개. `글로벌`(`glb`, 합포 재개)과 `MASS 정산`
 * (`mass`, 반품 정산 재발행)을 통째로 뺐다. 두 조치만 `hasAuthority('MASTER')` 라(핸드오프 §2.3
 * 권한 매트릭스) 이 콘솔의 대상 사용자인 `MOMS_ENG` 개발자 계정으로는 **어차피 401 이다** —
 * 누를 수 없는 버튼을 온콜 도구에 두면 장애 중에 "왜 안 되지" 를 한 번 더 겪게 만든다.
 * 12505 에서 그 두 API 의 인가가 `MOMS_ENG` 로 교체되면 그때 되살리는 편이 맞다(되살릴 코드는
 * 이 커밋 이전 히스토리에 그대로 있다).
 *
 * 범위 축소(2026-09-15, 사용자 결정) — 탭 3개 → 2개. `설정`(`cfg`) 탭도 통째로 뺐다. 남아 있던 두
 * 섹션 모두 **BE 가 반쪽만 있는** 것들이었다: 캐파 기본값(`s-cfg-capacity`)은 **조회 API 가 없어**
 * 첫 진입에 표가 비고 저장 응답으로만 현재 값을 알 수 있었으며, 잘못 넣어도 해제할 수단이 없었다
 * (삭제 엔드포인트는 있으나 UI 가 없어 배선하지 않았다). Kafka 컨슈머 정지(`s-cfg-consumer`)는
 * **전체 교체** 방식이라 조회가 실패한 상태에서 등록하면 기존 정지창이 전멸하는 구조였다(가드를
 * 붙여 막아 뒀지만 API 모양 자체가 그렇다). 온콜 도구에 두기엔 되돌릴 수 없는 쪽으로 기운다 —
 * BE 에 조회·해제가 갖춰지면 되살린다.
 *
 * 범위 축소(2026-09-15, 사용자 결정) — F-OPT 의 `재고 대조`(`s-fopt-compare`)·`바코드 보정`
 * (`s-fopt-fix`) 섹션도 뺐다. 바코드 보정이 고치는 것은 **마스터 상품(`master_item`)의 바코드**이지
 * 주문(`standard_order_product.barcode`)이 아니다 — 온콜 중에 필요한 건 주문 쪽 보정인데 이 화면은
 * 그걸 못 한다(어드민 MA02 상품관리와 같은 `ModifyMasterProductService` 를 부른다). 재고 대조는
 * 그 정정의 잠금 조건(옛 바코드에 잔량이 있으면 잠금)으로만 존재했고, 조회 자체는 어드민 st06 과
 * 중복이라 남길 이유가 사라졌다. 구·신 매핑(`POST /rfid-barcode-mappings`)에 조회 API 가 없어
 * 현재 매핑 상태를 화면에서 확인할 수 없었던 것도 같은 방향의 근거다.
 *
 * LOGISPRD-13034 — F-OPT 맨 위에 주문 조회(s-fopt-search)를 붙였다. 조회에서 고른 행은 아래 재발사
 * 섹션과 같은 확인 다이얼로그 · 같은 API 를 탄다.
 *
 * LOGISPRD-13035 — 탭 3개. 주문 Trace(trace) 추가 — refNo1 · 옵션번호로 15구간 흐름을 본다(moms-be PR #3818).
 *
 * LOGISPRD-13362 — 탭 4개. `MASS 정산`(mass, 반품 정산 재발행)을 되살렸다. 위에서 뺀 이유였던
 * `hasAuthority('MASTER')` 를 11517(moms-be PR #3988)이 `MOMS_ENG` 로 바꿨다. 합포 재개(glb)는
 * 여전히 MASTER 라 빠진 채로 둔다.
 *
 * LOGISPRD-14487 — 탭 6개. `ERP 복구`(erp, MCIS 컷오버 롤백 재송신)를 붙였다(moms-be PR #4568).
 * OIF_LO_GI 에는 출고·반품 행이 섞여 있어 입고 탭에 두면 "입고 롤백" 으로 읽힌다 — 탭을 따로 둔다.
 *
 * LOGISPRD-15746 — F-OPT 맨 아래에 워크플로 restart(s-fopt-restart)를 붙였다(moms-be PR #4886). V2 롤백 runbook 의
 * API 호출 단계를 이 화면에서 돈다.
 *
 * LOGISPRD-15812 — 탭 7개. `출고 설정`(obs, 디폴트 센터 우선순위 일괄 반영)을 붙였다. 위에서 뺀 `s-cfg-priority` 와 달리
 * 어드민 출고일정관리에 대응 기능이 없다. 어드민은 단건 `set-priority-type` 이라 지난 시각 슬롯을 못 바꾼다.
 *
 * LOGISPRD-15602 — 탭 8개. `창고`(wh)를 붙였다. 좌우 분할 — 왼쪽 `창고 목록`(비활성 포함, 클릭 선택)·오른쪽 `정책 편집`
 * (선택 창고의 활성 전환 + typed policy 6종). LES 대조·FC 등록(LOGISPRD-13890)은 후속 섹션이다.
 */
export const TABS: TabDef[] = [
  {
    key: 'disp',
    label: 'Dispatcher',
    heading: 'Dispatcher',
    sub: '정책 Hold',
    sections: [
      { id: 's-disp-holds', label: 'Hold 관리' },
      { id: 's-disp-create', label: '규칙 추가' },
      { id: 's-disp-held', label: '멈춘 워크플로' },
    ],
  },
  {
    key: 'fopt',
    label: 'F-OPT',
    heading: 'F-OPT',
    sub: '주문 조회 · SKU 백필 · 수동 재발사 · 워크플로 restart',
    sections: [
      { id: 's-fopt-search', label: '주문 조회' },
      { id: 's-fopt-sku-lookup', label: 'SKU 조회' },
      { id: 's-fopt-sku-fill', label: 'sku_id 백필' },
      { id: 's-fopt-redispatch', label: '재발사' },
      { id: 's-fopt-result', label: '재발사 결과' },
      { id: 's-fopt-restart', label: '워크플로 restart' },
      { id: 's-fopt-restart-result', label: 'restart 결과' },
    ],
  },
  {
    key: 'trace',
    label: '주문 Trace',
    heading: '주문 Trace',
    sub: '주문 한 건이 어느 테이블까지 흘러갔나',
    sections: [
      { id: 's-trace-query', label: '조회' },
      { id: 's-trace-orders', label: '주문' },
      { id: 's-trace-bizest', label: '비제스트 전송' },
      { id: 's-trace-flow', label: '구간 15개' },
    ],
  },
  {
    key: 'mass',
    label: 'MASS 정산',
    heading: 'MASS 정산',
    sub: '반품입고 정산 이벤트 재발행',
    sections: [
      { id: 's-mass-republish', label: '반품 정산 재발행' },
      { id: 's-mass-result', label: '재발행 결과' },
    ],
  },
  {
    key: 'inb',
    label: '입고',
    heading: '입고',
    sub: '반품 원주문 재매핑 · 글로벌 입고확정 롤백',
    sections: [
      { id: 's-inb-origin-remap', label: '반품 원주문 재매핑' },
      { id: 's-inb-global-rollback', label: '글로벌 입고 롤백' },
      { id: 's-inb-global-reconfirm', label: '글로벌 재확정' },
    ],
  },
  {
    key: 'erp',
    label: 'ERP 복구',
    heading: 'ERP 복구',
    sub: 'MCIS 컷오버 롤백 재송신',
    sections: [{ id: 's-erp-mcis-rollback-resend', label: 'MCIS 컷오버 롤백 재송신' }],
  },
  {
    key: 'obs',
    label: '출고 설정',
    heading: '출고 설정',
    sub: '디폴트 센터 우선순위 일괄 반영',
    sections: [
      { id: 's-obs-default-priority', label: '디폴트 우선순위 반영' },
      { id: 's-obs-default-priority-result', label: '반영 결과' },
    ],
  },
  {
    key: 'wh',
    label: '창고',
    heading: '창고',
    sub: '창고 목록·활성 전환·정책 편집',
    sections: [
      { id: 's-wh-list', label: '창고 목록' },
      { id: 's-wh-policy', label: '정책 편집' },
    ],
  },
]

export const DEFAULT_TAB: TabKey = 'disp'

/**
 * LOGISPRD-12506 (어드민 편입) — 콘솔 주소의 해시 네임스페이스.
 *
 * 콘솔은 어드민 라우트가 됐지만 경로가 아니라 **해시**(`/#console`)로 진입한다. 배포된 호스트
 * (`*.moms.musinsalogistics.co.kr`)의 CDN 이 `/` 외의 경로를 전부 API 오리진으로 보내기 때문이다 —
 * `/console` 같은 경로로 만들면 새로고침·북마크·주소 공유가 전부 `ERROR_UNAUTHORIZED` JSON 으로
 * 떨어진다(어드민의 다른 화면들이 지금 겪는 문제와 같다). 해시는 서버로 전송되지 않으므로 경로가
 * 항상 `/` 로 유지되고, CDN 은 평소처럼 어드민 `index.html` 을 준다.
 *
 * 그래서 탭도 같은 해시 안에 하위 경로로 들어간다: `#console` · `#console/fopt`.
 * `parseTabFromHash` 는 접두사 없는 형식(`#fopt`)도 해석한다 — 탭 키만 남은 값을 그대로 읽는
 * 분기라, 네임스페이스 없이 탭만 넘기는 호출부가 생겨도 기본 탭으로 떨어지지 않는다.
 *
 * 네임스페이스 상수(`CONSOLE_HASH_ROOT`)와 "이 해시가 콘솔인가" 판정(`isConsoleHash`)은
 * `@/utils/consoleEntry` 에 있다 — 어드민 메인 번들(`App.tsx`)이 그 판정을 써야 하는데, 콘솔
 * 모듈에 두면 판정 하나 때문에 콘솔 코드가 지연 청크에서 메인 번들로 끌려 나온다.
 */

const stripHash = (hash: string): string => hash.replace(/^#/, '')

/** 탭 키로 주소에 쓸 해시를 만든다(`#console/fopt`). */
export const buildConsoleHash = (tab: TabKey): string => `#${CONSOLE_HASH_ROOT}/${tab}`

export const parseTabFromHash = (hash: string): TabKey => {
  const value = stripHash(hash)
  const key = value.startsWith(`${CONSOLE_HASH_ROOT}/`) ? value.slice(CONSOLE_HASH_ROOT.length + 1) : value
  return TABS.some((t) => t.key === key) ? (key as TabKey) : DEFAULT_TAB
}
